import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, Heart, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/uploads/')) return `${BACKEND_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${BACKEND_URL}/api${imagePath}`;
  return imagePath;
};

const WishlistPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/wishlist/user/${user.user_id}`);
      if (res.data.success) {
        setProperties(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching wishlist:', err);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/wishlist' } });
      return;
    }
    fetchWishlist();
  }, [isAuthenticated, authLoading, navigate, fetchWishlist]);

  const removeFromWishlist = async (propertyId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/wishlist/toggle`, {
        user_id: user.user_id,
        property_id: propertyId
      });
      setProperties(prev => prev.filter(p => p.id !== propertyId));
      toast.success('Removed from wishlist');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6">
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to home
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="wishlist-title">Wishlists</h1>
        <p className="text-gray-600 mb-8">Your saved homestays</p>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#7CB342]"></div>
            <p className="mt-4 text-gray-500">Loading your wishlist...</p>
          </div>
        ) : properties.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Heart className="h-14 w-14 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No saved properties</h3>
              <p className="text-gray-500 mb-6">Tap the heart icon on any property to save it here.</p>
              <Button onClick={() => navigate('/')} className="bg-[#7CB342] hover:bg-[#6da036] text-white">
                Explore Homestays
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card
                key={property.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                data-testid="wishlist-card"
              >
                <div className="relative">
                  <img
                    src={getImageUrl(property.images?.[0])}
                    alt={property.title}
                    className="w-full h-52 object-cover"
                    onClick={() => navigate(`/property/${property.slug || property.id}`)}
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400'; }}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromWishlist(property.id); }}
                    className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2 shadow transition-colors"
                    data-testid="remove-wishlist-btn"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
                <CardContent className="p-4" onClick={() => navigate(`/property/${property.slug || property.id}`)}>
                  <h3 className="font-semibold text-gray-900 truncate">{property.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{property.location}, {property.country || 'India'}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-semibold text-[#7CB342]">₹{property.price?.toLocaleString()}<span className="text-gray-500 font-normal text-sm"> / night</span></span>
                    <span className="text-xs text-gray-400">{property.guests} guests · {property.bedrooms} beds</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
